import React, { useState } from "react";
import {
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Text,
  Stepper,
  Step,
  StepIndicator,
  StepStatus,
  StepIcon,
  Progress,
  useSteps,
  InputGroup,
  InputRightElement,
  FormErrorMessage,
  FormHelperText,
  Toast
} from "@chakra-ui/react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useRegisterStore } from '../store';
import { useCreateUserMutation } from '../graphql/types-and-hooks';
import { logError } from './logger'; 
import { useAlert } from "./AlertProvider";
import Step8Login from "./BookingSteps/Step8_Login";
import { servicebookingFlowStore } from "../store/ServiceBookingFlowStore";
    // eslint-disable-next-line
interface RegisterPasswordStepProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseUserCreate: () => void;  
}

const RegisterPasswordStep: React.FC = () => {
  const [show, setShow] = useState(false);
  const [password, setPassword] = useState(""); 
  const [confirmPassword, setConfirmPassword] = useState(""); 
  const [passwordError, setPasswordError] = useState(""); 
  const { showAlert } = useAlert();
  const [isRegistered, setIsRegistered] = useState(false);

  const [createUserMutation, { loading: createUserLoading }] =
    useCreateUserMutation();

  const [personalDetails] = useRegisterStore(state => [
    state.personalDetails,
    state.resetData,
  ]);

  const handleClick = () => setShow(!show);

  const validatePassword = (password: string) => {
    // eslint-disable-next-line
    const regex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[\^$*.{}\[\]()"?!@#%&/\\,><':;|_~`+=-]).{8,}$/;
    return regex.test(password);
  };

  const isServiceBooking = servicebookingFlowStore.isServiceBooking;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      showAlert({
        severity: "error",
        title: "Whoops",
        message: "Passwords do not match",
      })
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError("Password must contain at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character (^$*.[]{}()?\\-\"!@#%&/\\,><':;|_~`+=)");
      return;
    }

    try {
      const result = await createUserMutation({
        variables: {
          input: {
            email: personalDetails?.email,
            password: password,
            first_name: personalDetails?.firstName,
            last_name: personalDetails?.lastName,
            dob: personalDetails?.dateOfBirth,
            gender: personalDetails?.gender,
            phone_number: personalDetails?.mobileNumber,
          },
        },
      });

      console.log("User created successfully:", result);
      logError.info('User created successfully', { user: personalDetails?.email });

      // GTAG EVENT
      (window as any).gtag('event', 'auth', {
        event: 'created_account'
      });

      Toast({
        title: 'Family member removed successfully.',
        status: 'success',
        duration: 9000,
        isClosable: true,
      });

      setIsRegistered(true);
      
    } catch (error) {
      console.error("Error creating user:", error);
      logError.error('Error creating user', { error, user: personalDetails?.email });

      if (
        error?.graphQLErrors?.[0]?.extensions?.code ===
        'USER_ALREADY_EXISTS_ERROR'
      ) {
        showAlert({
          severity: "error",
          title: "Whoops",
          message: "User already exists"
        })
      } else {
        showAlert({
          severity: "error",
          title: "Whoops",
          message: "Registration error"
        })
      }
    }
  };

  

  const steps = [
    { title: 'First', description: 'User Details' },
    { title: 'Second', description: 'Create password' },
    { title: 'Third', description: 'Confirm User' },
  ];

  const { activeStep } = useSteps({
    index: 2,
    count: steps.length,
  });

  const max = steps.length - 1;
  const progressPercent = (activeStep / max) * 100;

  if (isRegistered) {
    return <Step8Login />;
  }

  return (
    <>
            <Box position='relative' maxWidth={'380px'} width={{base:'100%', "md":'50%'}} >
              <Stepper size='sm' index={activeStep} gap='20'>
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepIndicator bg='white'>
                      <StepStatus complete={<StepIcon />} />
                    </StepIndicator>
                  </Step>
                ))}
              </Stepper>
              <Progress
                value={progressPercent}
                position='absolute'
                height='3px'
                width='full'
                top='10px'
                zIndex={-1}
              />
            </Box>
            
            <Box flex="1" p={4} mt={5} maxWidth={'400px'} width={{base:'100%', "md":'50%'}}>
              <Text fontSize="md">
                Enter and confirm a password for your account.
              </Text>
              
              <form onSubmit={onSubmit}>
                <Input type="hidden" autoComplete="username" value={personalDetails?.email} />

                <FormControl mb={4} mt={8} isInvalid={passwordError !== ""}>
                  <FormLabel>New Password</FormLabel>
                  <InputGroup size="md">
                    <Input
                      pr="4.5rem"
                      type={show ? "text" : "password"}
                      placeholder="Enter password"
                      borderRadius="30px"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError("");
                      }}
                      autoComplete="new-password"
                    />
                    <InputRightElement width="4.5rem">
                      <Button
                        h="1.75rem"
                        size="sm"
                        onClick={handleClick}
                        borderRadius="30px"
                      >
                        {show ? <FiEyeOff /> : <FiEye />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{passwordError}</FormErrorMessage>
                  <FormHelperText>New password must contain at least 8 characters, including an uppercase letter, a lowercase letter, a number, and a special character</FormHelperText>
                </FormControl>

                <FormControl mb={4} mt={8}>
                  <FormLabel>Confirm New Password</FormLabel>
                  <InputGroup size="md">
                    <Input
                      pr="4.5rem"
                      type={show ? "text" : "password"}
                      placeholder="Enter password"
                      borderRadius="30px"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <InputRightElement width="4.5rem">
                      <Button
                        h="1.75rem"
                        size="sm"
                        onClick={handleClick}
                        borderRadius="30px"
                      >
                        {show ? <FiEyeOff /> : <FiEye />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                <Button
                  bg={isServiceBooking ? servicebookingFlowStore?.configurationDetails?.colorScheme?.buttonBg : "#B92F2F"}
                  color="#fff"
                  mt={5}
                  width="100%"
                  borderRadius="30px"
                  mb="20px"
                  type="submit"
                  isLoading={createUserLoading}
                >
                  Save Password
                </Button>
              </form>
            </Box>
          
    </>
  );
};

export default RegisterPasswordStep;
